import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe, Users, Bot, UserPlus, Link2, KeyRound } from 'lucide-react';
import type { ChallengeSettings } from '@draughts/shared';
import type { OnlineUser } from '../../store/online-store';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { isGuestPlayer } from '../../utils/guest';
import { usePreferencesStore } from '../../store/preferences-store';
import { AI_DIFFICULTIES, AI_DIFFICULTY_LABEL_KEY, AI_DIFFICULTY_DOT, type AIDifficulty } from '../../utils/ai-difficulty';
import { getLastAiDifficulty, setLastAiDifficulty } from '../../utils/last-used-play';
import ChallengeOptionsFields from '../online/ChallengeOptionsFields';
import OnlinePlayersList from '../online/OnlinePlayersList';
import { cePannelloAperto } from '../game/FullscreenPanel';
import { ratingBucketFrom } from '../../utils/rating-bucket';
import { usePunteggioAvversario } from '../../hooks/usePunteggioAvversario';
import { getRatingClass } from '../../utils/rating';
import { variantShortLabel } from '../../utils/variant-labels';
import VariantSelect from '../profile/VariantSelect';
import type { AIGameOptions } from '../dashboard/DashboardPlayAIModal';
import '../dashboard/PlayModals.css';
import '../online/challenge-form.css';
// `.guest-challenge-notice` e `.guest-notice-login` sono classi globali, ma vivono
// in questo file, che solo ConnectedUsersModal importa. Questo foglio e' montato
// anche dal PlayOverlay globale di App, su schermate dove quel componente puo' non
// essere nel chunk caricato: senza l'import il riquadro uscirebbe senza stile.
import '../online/ConnectedUsersModal.css';
// Stessa ragione: `.player-rating` (e le sue sette classi di fascia) e' globale ma
// dichiarata nel foglio della lista dei collegati. Qui serve anche a lista
// SMONTATA — la riga dell'avversario gia' scelto — e appoggiarsi al fatto che
// quel componente sia importato qui sopra e' una garanzia che il primo
// spezzettamento del bundle porterebbe via senza far rumore.
import '../online/OnlinePlayersList.css';
import './CustomTableOverlay.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Variante scelta in Gioca: il foglio parte da lì, non da capo. */
  defaultVariant: string;
  /** Cadenza di partenza (la hero della dashboard ha già la sua). */
  defaultTimeControl?: string;
  /** Un ospite può ospitare solo un'amichevole — lo stesso declassamento del server. */
  isGuest: boolean;
  busy: boolean;
  /**
   * Pubblica il tavolo. Torna `true` se è andata: allora il foglio si chiude.
   *
   * Facoltativa come le altre: senza, il tavolo pubblico non viene offerto. È il
   * caso dell'editor, che ha una posizione composta da sfidare e nient'altro.
   */
  onCreate?: (settings: ChallengeSettings) => Promise<boolean>;
  /**
   * Avvia una partita in presenza: due persone vere a questo dispositivo.
   *
   * Quando manca, la voce non viene offerta. Stessa regola per le altre tre qui
   * sotto: un montaggio che non puo' onorare una scelta non deve proporla.
   */
  onPlayInPerson?: (settings: ChallengeSettings) => void;
  /** Avvia una partita contro il computer, con le stesse opzioni della Sfida AI. */
  onPlayAI?: (variant: string, difficulty: AIDifficulty, options: AIGameOptions) => void;
  /** Invia una sfida diretta al giocatore scelto nella lista. */
  /** `null` se la sfida e' partita, altrimenti la CHIAVE i18n del motivo del no. */
  onSendChallenge?: (userId: string, settings: ChallengeSettings) => Promise<string | null>;
  /** Crea un link da mandare a un amico che nell'app non c'e'. */
  onCreateChallengeLink?: (settings: ChallengeSettings) => void;
  /** Apre le tabelle di apertura dentro l'app (vedi ChallengeOptionsFields). */
  onOpenOpeningTables?: (variant: string) => void;
  /**
   * Apre il modale d'accesso: la maniglia dell'invito che un ospite vede al posto
   * della Sfida diretta. Opzionale — un montaggio che non ha nessuna porta da far
   * passare mostra il solo messaggio invece di promettere un bottone che non c'e'.
   */
  onSignIn?: () => void;
  /**
   * Guarda la partita in corso di chi e' gia' impegnato, dalla riga della lista.
   *
   * L'ORDINE conta, e lo decide chi passa la callback: prima si chiude il foglio,
   * poi si naviga al giro dopo. Questo foglio tiene una voce di cronologia che
   * estrae in chiusura, e spingere una schermata nuova nello stesso tick correrebbe
   * contro quella estrazione.
   */
  onSpectate?: (user: OnlineUser) => void;
  /** Avversario gia' scelto altrove (la lista dei collegati). */
  preselectedUser?: OnlineUser;
  /** Destinazione di partenza; senza, la prima che questo montaggio sa raggiungere. */
  defaultOpponent?: Opponent;
  /**
   * Posizione di partenza (FEN) da cui giocare, dall'EDITOR. Blocca la variante e
   * nasconde le righe dell'apertura.
   */
  position?: string;
  /** Forza l'amichevole comunque: le posizioni composte non sono mai valide. */
  lockRated?: boolean;
  /**
   * Semina i menu dal ricordo locale e ci riscrive ogni scelta. L'editor la spegne:
   * con una posizione composta la variante e' quella della posizione, e ripescare
   * l'ultima usata mostrerebbe una pillola che dice il falso.
   */
  rememberSettings?: boolean;
}

/** Chi c'e' dall'altra parte. */
type Opponent = 'online' | 'computer' | 'challenge' | 'inperson';

/**
 * "Partita personalizzata" — il foglio dedicato.
 *
 * Prima era un'espansione dentro Gioca: otto righe di menu che si aprivano
 * sotto le cadenze, con il bottone "Apri il tavolo" spinto sotto la lista dei
 * tavoli. Qui le stesse righe hanno la loro schermata, e Gioca resta montato
 * dietro — chiudere questo foglio ci riporta, non ricarica niente.
 *
 * La riga "Avversario" e' quello che il foglio fa di suo: le stesse impostazioni
 * portano a quattro destinazioni diverse — un tavolo pubblico, il computer, un
 * giocatore scelto, la persona seduta di fronte. Prima le ultime tre vivevano in
 * tre modali separate che facevano ripartire la scelta da capo.
 *
 * Veste `play-online-modal`, la stessa pelle di Gioca e Chi è collegato.
 */
export default function CustomTableOverlay({
  isOpen,
  onClose,
  defaultVariant,
  defaultTimeControl,
  isGuest,
  busy,
  onCreate,
  onPlayInPerson,
  onPlayAI,
  onSendChallenge,
  onCreateChallengeLink,
  onOpenOpeningTables,
  onSignIn,
  onSpectate,
  preselectedUser,
  defaultOpponent,
  position,
  lockRated = false,
  rememberSettings = true,
}: Props) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ChallengeSettings | null>(null);
  const [difficulty, setDifficulty] = useState<AIDifficulty>(() => getLastAiDifficulty() ?? 'medium');
  const [selectedUser, setSelectedUser] = useState<OnlineUser | null>(preselectedUser ?? null);
  /**
   * La lista e' chiusa e al suo posto c'e' il nome scelto.
   *
   * Separata da `selectedUser` apposta: "Cambia" riapre la lista TENENDO la scelta,
   * cosi' la riga di prima resta evidenziata e si vede da dove si riparte. Azzerare
   * l'avversario, come si faceva qui, spegneva anche il primario e lasciava la
   * lista senza niente da indicare.
   */
  const [playersCollapsed, setPlayersCollapsed] = useState(!!preselectedUser);
  // Solo le destinazioni che questo montaggio sa raggiungere. Ogni callback
  // mancante toglie la sua voce — anche il tavolo pubblico, che dall'editor non
  // si puo' aprire. Con una sola voce non c'e' niente da scegliere e la riga non
  // compare, ma il resto del modulo resta: vedi `opponentRow` piu' sotto.
  const opponents = useMemo<Opponent[]>(() => [
    ...(onCreate ? ['online' as const] : []),
    ...(onPlayAI ? ['computer' as const] : []),
    ...(onSendChallenge ? ['challenge' as const] : []),
    ...(onPlayInPerson ? ['inperson' as const] : []),
  ], [onCreate, onPlayAI, onSendChallenge, onPlayInPerson]);

  // La destinazione di partenza e' quella chiesta da chi apre il foglio; senza,
  // la prima raggiungibile — che NON e' piu' per forza il tavolo pubblico.
  const [opponent, setOpponent] = useState<Opponent>(() => defaultOpponent ?? opponents[0] ?? 'online');

  /*
   * Il punteggio che la lista mostra segue il modulo: si sta scegliendo un
   * avversario PER QUESTA partita, e il numero utile e' il suo punteggio in
   * questa variante a questa cadenza. `settings` e' `null` finche' i campi non
   * si annunciano, quindi si parte dai default con cui il foglio e' stato
   * aperto; senza orologio (∞) non esiste colonna di rating e si ripiega sulla
   * cadenza preferita, che il chip in testata dichiara.
   */
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);
  const preferredCadence = usePreferencesStore((s) => s.preferredCadence);
  const ratingBucket = useMemo(
    () => ratingBucketFrom(
      settings?.variant ?? defaultVariant,
      settings?.timeControl ?? defaultTimeControl,
      { variant: preferredVariant, cadence: preferredCadence },
    ),
    [settings?.variant, settings?.timeControl, defaultVariant, defaultTimeControl, preferredVariant, preferredCadence],
  );

  const inPerson = opponent === 'inperson';
  const vsComputer = opponent === 'computer';
  const direct = opponent === 'challenge';

  /*
   * Il punteggio accanto al nome gia' scelto.
   *
   * Scelto l'avversario, la lista si chiude e con lei se ne va l'unica cosa che
   * rinfrescava i numeri: da li' in poi variante e cadenza si continuano a
   * cambiare due menu piu' sotto, e il punteggio dell'istantanea sarebbe quello
   * della colonna di prima. Il gancio se lo va a chiedere da se'.
   *
   * `attivo` e' falso ESATTAMENTE quando `OnlinePlayersList` e' montato (lista
   * aperta) o la destinazione non e' la sfida diretta: cosi' i due non chiedono
   * mai la stessa cosa insieme. `isOpen` ci vuole per forza — il foglio resta
   * montato anche da chiuso, il `return null` sta sotto i ganci. Ospiti e bot
   * punteggio non ne hanno: per loro non parte nemmeno una richiesta.
   */
  const punteggioAvversario = usePunteggioAvversario(
    selectedUser,
    ratingBucket,
    isOpen && direct && playersCollapsed && !!selectedUser && !isGuestPlayer(selectedUser),
  );
  /* Di quale punteggio si parla. Nella lista aperta lo dice il chip in testata;
     qui la riga e' gia' piena (nome, numero, «Cambia») e a 360px un secondo
     chip mangerebbe il nome, quindi la dichiarazione vive nel `title`. Chiavi
     gia' tradotte in tutte e otto le lingue: nessuna stringa nuova. */
  const etichettaBucket = `${t('online.ratingsBucket')} ${variantShortLabel(t, ratingBucket.variant)} · ${t(`leaderboard.${ratingBucket.cadence}`)}`;

  /**
   * Ri-semina dai prop ogni volta che il foglio SI APRE.
   *
   * Non basta seminare lo `useState`: il foglio resta montato anche da chiuso
   * (il `return null` sta sotto gli hook), quindi l'inizializzatore gira una
   * volta sola per tutta la vita della schermata che lo ospita. Senza questo,
   * chi apre la Sfida diretta su Ada, chiude, e la riapre su Bruno si ritrova
   * ancora Ada — l'avversario sbagliato, gia' pronto da sfidare.
   *
   * Tocca solo cio' che gli e' stato chiesto: i montaggi che non preselezionano
   * nessuno (il lobby) si tengono la loro scelta di prima, come hanno sempre fatto.
   */
  useEffect(() => {
    if (!isOpen) return;
    if (preselectedUser) {
      setSelectedUser(preselectedUser);
      setPlayersCollapsed(true);
    }
    if (defaultOpponent) setOpponent(defaultOpponent);
  }, [isOpen, preselectedUser, defaultOpponent]);

  useOverlayBackButton(isOpen, onClose);

  // Esc, come ogni altro foglio di gioco. Gioca sta sotto e ha lo stesso
  // listener: è LUI a tirarsi indietro finché questo foglio è aperto, così Esc
  // chiude uno strato alla volta.
  //
  // E questo foglio fa lo stesso con chi sta sopra di LUI. La guardia e' nata quando
  // la lista qui dentro apriva il profilo di un giocatore (`FullscreenPanel`): gli
  // strati erano tre e — misurato — un Esc solo ne chiudeva due, tornando indietro di
  // un passo piu' del voluto. Il profilo da qui non si apre piu' (la riga sceglie
  // l'avversario e basta), ma la guardia resta: e' la rete per qualunque pannello a
  // tutto schermo che domani si apra da questo foglio, e costa una chiamata.
  // `cePannelloAperto()` si chiama DENTRO il gestore, non nelle dipendenze: la pila
  // va letta al momento del tasto, non al montaggio dell'effetto.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || cePannelloAperto()) return;
      onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  /* La chiave i18n dell'ultimo rifiuto del server, o `null`. Si azzera da sola
     quando si cambia avversario: il motivo riguardava QUELLO di prima. */
  const [erroreInvio, setErroreInvio] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!settings) return;
    // Le tre destinazioni che non passano dal lobby non hanno niente da pubblicare
    // e nessuno da aspettare: partono subito e il foglio si chiude.
    //
    // L'ordine — prima il callback, poi onClose() — e' lo stesso della partita in
    // presenza. NOTA: quella sequenza chiude due overlay nello stesso commit e
    // ognuna delle due chiusure manda indietro la cronologia (consumeOverlay ->
    // history.back()), mentre il cambio di schermata ne spinge una nuova: il
    // risultato e' che dopo queste transizioni un «indietro» esce dall'app. E' un
    // difetto GIA' presente sulla partita in presenza, verificato a mano, e vive
    // in useOverlayBackButton/useBackNavigation — non qui. Allinearsi a quella
    // sequenza e' deliberato: quando si aggiustera' la', si aggiusteranno tutte
    // insieme.
    if (inPerson) {
      onPlayInPerson?.(settings);
      onClose();
      return;
    }
    if (vsComputer) {
      onPlayAI?.(settings.variant, difficulty, {
        opening: settings.opening,
        color: settings.color,
        ruleMode: settings.gameMode,
      });
      onClose();
      return;
    }
    if (direct) {
      if (!selectedUser) return;
      /*
       * Come il tavolo pubblico qui sotto: si chiude solo se e' andata.
       *
       * Il server ha nove modi di rifiutare una sfida — l'avversario ha appena
       * cominciato una partita, si e' scollegato, e' in un torneo; oppure una
       * sfida in volo ce l'hai gia' tu. Chiudere comunque voleva dire portare via
       * l'unico posto dove il motivo si puo' leggere E rimediare: qui dentro si
       * cambia avversario senza ricominciare da capo.
       */
      const chiaveErrore = (await onSendChallenge?.(selectedUser.userId, settings)) ?? null;
      setErroreInvio(chiaveErrore);
      if (!chiaveErrore) onClose();
      return;
    }
    if (!onCreate) return;
    const ok = await onCreate(settings);
    if (ok) onClose();
  }, [
    inPerson, vsComputer, direct, difficulty, selectedUser,
    onClose, onCreate, onPlayInPerson, onPlayAI, onSendChallenge, settings,
  ]);

  const handleCreateLink = useCallback(() => {
    if (!settings) return;
    onCreateChallengeLink?.(settings);
    onClose();
  }, [onClose, onCreateChallengeLink, settings]);

  /*
   * Il fuoco dentro il dialogo, Tab che non ne esce, e alla chiusura il fuoco
   * torna a chi l'aveva. Ruolo, nome ed Escape c'erano già; il fuoco no — e senza
   * di lui la tabulazione continua nella pagina SOTTO, che è ancora lì dietro il
   * velo, su bottoni che nessuno vede.
   *
   * Chiamato PRIMA del `return null` qui sotto — un hook non può stare dietro a
   * una condizione — con `isOpen` come interruttore. E senza `onClose`: l'Escape
   * lo gestisce già il componente, passarlo anche all'hook chiuderebbe due volte.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen);

  if (!isOpen) return null;

  const opponentLabel = (v: string): string => {
    if (v === 'computer') return t('dashboard.hero.ctaVsComputer');
    if (v === 'challenge') return t('online.challenge.sendChallenge');
    if (v === 'inperson') return t('lobby.opponentInPerson');
    return t('lobby.opponentOnline');
  };

  // `Swords` è escluso di proposito: in tutta l'app vuol dire "arena".
  const opponentLeading = (v: string) => {
    if (v === 'computer') return <Bot size={14} aria-hidden="true" />;
    if (v === 'challenge') return <UserPlus size={14} aria-hidden="true" />;
    if (v === 'inperson') return <Users size={14} aria-hidden="true" />;
    return <Globe size={14} aria-hidden="true" />;
  };

  /**
   * Un ospite non può sfidare nessuno: lo rifiuta il server (sfida E link), e non
   * ha senso fargli compilare un modulo che non porta da nessuna parte.
   */
  const guestBlocked = direct && isGuest;

  /**
   * La riga dell'avversario, fuori dal JSX perche' la montano DUE rami: quello
   * normale la infila fra le impostazioni, quello dell'ospite la tiene da sola —
   * senza, l'unica via d'uscita sarebbe chiudere il foglio.
   *
   * Con una sola destinazione non c'e' niente da scegliere e la riga non esiste.
   * Il SELETTORE DEL GIOCATORE invece resta: sono due cose diverse, e tenerle
   * insieme voleva dire che il montaggio della sola Sfida diretta — l'editor —
   * perdeva anche la lista, cioe' l'unico modo di scegliere chi sfidare.
   */
  const opponentRow = opponents.length > 1 && (
    <div className="challenge-setting">
      <label className="challenge-setting-label">{t('lobby.opponent')}</label>
      <VariantSelect
        value={opponent}
        onChange={(v) => setOpponent(v as Opponent)}
        variants={opponents}
        label={opponentLabel}
        dotColor={() => 'transparent'}
        renderLeading={opponentLeading}
        ariaLabel={t('lobby.opponent')}
      />
      {inPerson && (
        <p className="challenge-custom-note">{t('lobby.inPersonNote')}</p>
      )}
    </div>
  );

  /*
   * Non si chiude niente prima di aprire l'accesso: il modale sale da se' sopra il
   * foglio (`.auth-modal-overlay` sta a 10500) ed e' lui che il gesto «indietro»
   * chiude per primo. Sparecchiare vorrebbe dire chiudere due overlay nello stesso
   * commit — la stessa ragione scritta in ConnectedUsersModal.
   */
  const signInButton = onSignIn && (
    <button type="button" className="guest-notice-login" onClick={onSignIn}>
      <KeyRound size={16} aria-hidden="true" />
      {t('auth.signIn')}
    </button>
  );

  /**
   * Il titolo dice cosa sa fare QUESTO montaggio, e non cambia mentre si scorre la
   * tendina: un'intestazione che salta sotto gli occhi a ogni voce e' peggio di una
   * ferma.
   *
   * Si intitola "Sfida diretta" solo quando mandare una sfida e' l'UNICA cosa che
   * si puo' fare da qui — il montaggio dell'editor. Legarlo al numero di
   * destinazioni e non a QUALE fosse il malinteso di prima: un montaggio col solo
   * tavolo pubblico ne ha una sola anche lui, e si ritrovava a chiamarsi "Sfida
   * diretta" mentre l'unico bottone diceva "Apri il tavolo".
   */
  const soloSfida = opponents.length === 1 && opponents[0] === 'challenge';
  const title = soloSfida ? t('online.challenge.sendChallenge') : t('lobby.customGame');

  const submitLabel = vsComputer
    ? t('dashboard.hero.ctaVsComputer')
    : direct
      ? t('online.challenge.send')
      : inPerson
        ? t('lobby.startInPerson')
        : t('lobby.openTable');

  const submitDisabled = !settings
    || (direct && !selectedUser)
    // `busy` e' il lobby che sta pubblicando un tavolo: non ha voce in capitolo
    // sulle partite che non passano dal server.
    || (opponent === 'online' && busy);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal dashboard-modal play-online-modal custom-table-overlay"
        ref={riferimentoDialogo}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="play-online-head">
          <h2>{title}</h2>
          <button type="button" className="play-online-close" aria-label={t('common.cancel')} onClick={onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/*
          * Da ospite, la Sfida diretta non ha un modulo da compilare: il server
          * rifiuta sia la sfida sia il link, quindi restano la riga per cambiare
          * idea e l'invito, con la sua maniglia.
          */}
        <div className="play-online-body">
          {guestBlocked ? (
            <>
              {opponentRow && <div className="challenge-settings">{opponentRow}</div>}
              <div className="guest-challenge-notice">
                <p>{t('online.challenge.guestCannotChallenge')}</p>
                {signInButton}
              </div>
            </>
          ) : (
            <ChallengeOptionsFields
              onChange={setSettings}
              defaultVariant={defaultVariant}
              defaultTimeControl={defaultTimeControl}
              position={position}
              rememberSettings={rememberSettings}
              hotSeat={inPerson}
              vsComputer={vsComputer}
              // Solo la sfida diretta ha un destinatario, ed è l'unica in cui
              // "solo valida" può essere una promessa che l'altro non può mantenere.
              opponent={direct && selectedUser ? { isGuest: isGuestPlayer(selectedUser) } : undefined}
              onOpenOpeningTables={onOpenOpeningTables && ((v) => { onOpenOpeningTables(v); onClose(); })}
              // Un ospite può ospitare solo un'amichevole: lo stesso declassamento
              // che applicherebbe il server, detto prima — così il tavolo non
              // annuncia mai qualcosa che non può mantenere. Vale per il tavolo
              // pubblico: le altre tre destinazioni o non toccano il punteggio
              // (computer, in presenza) o sono già chiuse a un ospite (sfida diretta).
              lockRated={lockRated || (isGuest && opponent === 'online')}
              // Lo stesso invito della Sfida diretta, non una notina grigia: la
              // situazione e' la stessa a una voce di menu di distanza. Qui pero'
              // il modulo RESTA — l'ospite puo' giocare, solo non conta.
              lockRatedNote={(
                <div className="guest-challenge-notice">
                  <p>{t('online.challenge.link.guestCasualNote')}</p>
                  <p>{t('online.challenge.guestCasualInvite')}</p>
                  {signInButton}
                </div>
              )}
              headerSlot={(opponentRow || vsComputer || direct) ? (
                <>
                  {opponentRow}

                  {vsComputer && (
                    <div className="challenge-setting">
                      <label className="challenge-setting-label">{t('home.difficulty')}</label>
                      <VariantSelect
                        value={difficulty}
                        onChange={(d) => { setDifficulty(d as AIDifficulty); setLastAiDifficulty(d as AIDifficulty); }}
                        variants={AI_DIFFICULTIES}
                        label={(d) => t(`game.modes.${AI_DIFFICULTY_LABEL_KEY[d as AIDifficulty]}`)}
                        dotColor={(d) => AI_DIFFICULTY_DOT[d as AIDifficulty]}
                        ariaLabel={t('home.difficulty')}
                      />
                    </div>
                  )}

                  {/* La sfida diretta ha un destinatario da scegliere. Un ospite qui
                      non ci arriva: il suo ramo e' sopra, e non monta questo modulo.
                      Niente bottone "espandi a tutto schermo": quello serviva al vecchio
                      modale, che aveva colonne ad altezza fissa — qui il corpo del foglio
                      scorre gia' tutto. */}
                  {direct && (selectedUser && playersCollapsed ? (
                    <div className="challenge-preselected">
                      <span className="challenge-preselected-player">
                        <span className="challenge-preselected-name">{selectedUser.username}</span>
                        {/* Il numero compare solo a timbro combaciante: il gancio
                            restituisce `null` finche' il punteggio letto non e'
                            quello di QUESTA variante a QUESTA cadenza. Meglio
                            nessun numero per mezzo secondo che il numero di
                            un'altra colonna accanto a un nome. */}
                        {punteggioAvversario != null && (
                          <span
                            className={`player-rating ${getRatingClass(punteggioAvversario).toLowerCase()}`}
                            title={etichettaBucket}
                          >
                            {punteggioAvversario}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="challenge-preselected-change"
                        onClick={() => setPlayersCollapsed(false)}
                      >
                        {t('online.challenge.changeOpponent')}
                      </button>
                    </div>
                  ) : (
                    <div className="challenge-players-section">
                      <div className="players-scroll">
                        {/*
                          * Qui la lista e' un SELETTORE, non una vetrina.
                          *
                          * La riga intera sceglie l'avversario: niente bottone
                          * «Sfida» (non sfidava nessuno — sceglieva, e la sfida
                          * parte da «Invia» qui sotto) e niente porta sul profilo,
                          * che si frapponeva al gesto per cui si e' aperto questo
                          * pannello. Il profilo di un giocatore si guarda dal
                          * pannello dei collegati, dove la riga non deve scegliere
                          * niente.
                          */}
                        <OnlinePlayersList
                          selectionMode
                          /* Il motivo del no si azzera col cambio d'avversario: parlava di
                             QUELLO di prima, e lasciarlo sotto il bottone lo farebbe
                             leggere come se riguardasse questo. */
                          onChallengePlayer={(u) => { setSelectedUser(u); setPlayersCollapsed(true); setErroreInvio(null); }}
                          onSpectatePlayer={onSpectate}
                          selectedUserId={selectedUser?.userId}
                          ratingBucket={ratingBucket}
                        />
                      </div>
                    </div>
                  ))}
                </>
              ) : undefined}
            />
          )}
        </div>

        {/* Niente azioni nel ramo dell'ospite: non c'e' niente da premere. */}
        {!guestBlocked && (
          <div className="modal-actions">
            {/* Il motivo del no, sopra il bottone che l'ha ricevuto. `role=alert`
                perche' compare dopo un gesto e chi usa un lettore di schermo deve
                sentirlo senza andarlo a cercare. */}
            {erroreInvio && (
              <p className="custom-table-errore" role="alert">{t(erroreInvio)}</p>
            )}
            <button
              type="button"
              className="primary"
              onClick={() => void handleSubmit()}
              disabled={submitDisabled}
            >
              {submitLabel}
            </button>
            {/* Per un amico che nell'app non c'è: il link non ha un destinatario,
                quindi non aspetta che si scelga qualcuno dalla lista. */}
            {direct && onCreateChallengeLink && (
              <button
                type="button"
                className="invite-link-btn"
                onClick={handleCreateLink}
                disabled={!settings}
              >
                <Link2 size={18} aria-hidden="true" />
                {t('online.challenge.link.inviteFriend')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
